import React, { useState, useEffect } from "react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import dayjs from "dayjs";
import VitalService from "../../Services/VitalService";

import Temp from "../../assets/newicons/temp.svg";
import Blood from "../../assets/newicons/blood1.svg";
import Res from "../../assets/newicons/respirate.png";
import Pulse from "../../assets/newicons/pulse.svg";
import Heart from "../../assets/newicons/heart.svg";
import Height from "../../assets/newicons/height_2.svg";
import Weight from "../../assets/newicons/monitor_weight_2.svg";
import BMI from "../../assets/newicons/bmi.svg";

const vitalsConfig = [
  { name: "Temperature", code: "TP", unit: "°F", color: "#24CC8C", bg: "#E9FFEE", icon: Temp },
  { name: "Blood Pressure", code: ["BPS", "BPD"], unit: "mmHg", color: "#FF9E9E", bg: "#FFE2E8", icon: Blood },
  { name: "Respiratory Rate", code: "RR", unit: "rpm", color: "#9DCBFF", bg: "#E1EDFF", icon: Res },
  { name: "Pulse Ox.", code: "SPO2", unit: "%", color: "#A8ECFF", bg: "#DFF8FF", icon: Pulse },
  { name: "Heart Rate", code: "HR", unit: "bpm", color: "#FFD3B4", bg: "#FFE9DA", icon: Heart },
  { name: "Height", code: "HI", unit: "cm", color: "#DDC0FF", bg: "#FFF5D2", icon: Height },
  { name: "Weight", code: "WE", unit: "kg", color: "#FFC0D7", bg: "#FFE9F3", icon: Weight },
  { name: "BMI", code: "BMI", unit: "kg/m²", color: "#FFEDC2", bg: "#FAECFF", icon: BMI },
];

const VitalStatCarousel = ({ patientId, vitalsData }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);

  const currentVital = vitalsConfig[currentIndex];

  useEffect(() => {
    fetchGraphData();
  }, [currentIndex, patientId]);

  const fetchGraphData = async () => {
    if (!patientId) return;
    setLoadingChart(true);
    try {
      const fromDate = dayjs().subtract(7, "day").startOf("day");
      const toDate = dayjs().endOf("day");
      const code = Array.isArray(currentVital.code) ? currentVital.code : [currentVital.code];

      const payload = {
        patientId,
        vitalCode: code,
        fromDate: fromDate.format("MM/DD/YYYY"),
        toDate: toDate.format("MM/DD/YYYY"),
        status: 1,
      };

      const result = await VitalService.getVitalForGraph(payload);
      const allVitals = result?.data || [];

      const valuesByCode = {};
      allVitals.forEach((item) => {
        const dayKey = dayjs(item.recordedDateTime).format("YYYY-MM-DD");
        if (!valuesByCode[item.vitalCode]) valuesByCode[item.vitalCode] = {};
        valuesByCode[item.vitalCode][dayKey] = item.value;
      });

      const data = [];
      let temp = fromDate;
      while (temp.isSameOrBefore(toDate, "day")) {
        const key = temp.format("YYYY-MM-DD");
        data.push({
          day: temp.format("dd")[0],
          date: key,
          data1: valuesByCode["BPS"]?.[key] ?? null,
          data2: valuesByCode["BPD"]?.[key] ?? null,
          uv: valuesByCode[Array.isArray(currentVital.code) ? currentVital.code[0] : currentVital.code]?.[key] ?? null,
        });
        temp = temp.add(1, "day");
      }
      setChartData(data);
    } catch (error) {
      console.error("Error fetching graph data:", error);
    } finally {
      setLoadingChart(false);
    }
  };

  const getRecentValue = () => {
    if (!Array.isArray(vitalsData)) return "--";
    if (Array.isArray(currentVital.code)) {
      const s = vitalsData.find((v) => v.vitalCode === "BPS")?.value || "--";
      const d = vitalsData.find((v) => v.vitalCode === "BPD")?.value || "--";
      return `${s}/${d}`;
    }
    return vitalsData.find((v) => v.vitalCode === currentVital.code)?.value || "--";
  };

  const next = () => setCurrentIndex((prev) => (prev + 1) % vitalsConfig.length);
  const prev = () => setCurrentIndex((prev) => (prev - 1 + vitalsConfig.length) % vitalsConfig.length);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      if (!data) return null;
      const hasDual = data.data1 != null || data.data2 != null;
      const valueText = hasDual
        ? `${data.data1 ?? "-"}${data.data2 ? ` / ${data.data2}` : ""}`
        : (data.uv ?? "-");

      return (
        <div className="bg-white border border-gray-200 p-2 rounded-lg shadow-md">
          <p className="font-bold text-xs">{dayjs(data.date).format("dddd, YYYY-MM-DD")}</p>
          <p className="text-xs text-rose-500">Value: {valueText}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-2xl p-6 h-full flex flex-col justify-between shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-sm"
            style={{ backgroundColor: currentVital.bg }}
          >
            <img src={currentVital.icon} alt="" className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-gray-500 text-xs font-bold uppercase tracking-wider">{currentVital.name}</h3>
            <p className="text-2xl font-black text-gray-900 leading-tight">
              {getRecentValue()}
              <span className="text-sm font-medium text-gray-400 ml-1">{currentVital.unit}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={prev}
            className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <MdChevronLeft size={20} className="text-gray-400" />
          </button>
          <button
            onClick={next}
            className="w-8 h-8 rounded-full border border-gray-100 flex items-center justify-center hover:bg-gray-50 transition-colors"
          >
            <MdChevronRight size={20} className="text-gray-400" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-[200px]">
        {loadingChart ? (
          <div className="w-full h-full bg-gray-50 animate-pulse rounded-xl" />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={currentVital.color} stopOpacity={0.8} />
                  <stop offset="95%" stopColor={currentVital.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#374151", fontSize: 10 }}
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="basis"
                dataKey="uv"
                stroke={currentVital.color}
                strokeWidth={2}
                fill={currentVital.color}
                fillOpacity={0.2}
                connectNulls={true}
                dot={(props) => {
                  if (props.payload.uv != null) {
                    return (
                      <circle
                        cx={props.cx}
                        cy={props.cy}
                        r={4}
                        fill={currentVital.color}
                        stroke="#fff"
                        strokeWidth={1}
                      />
                    );
                  }
                  return null;
                }}
              />
              {Array.isArray(currentVital.code) && (
                <Area
                  type="basis"
                  dataKey="data2"
                  stroke="#82ca9d"
                  strokeWidth={2}
                  fill={"#82ca9d"}
                  fillOpacity={0.3}
                  connectNulls
                  dot={(props) =>
                    props.payload.data2 != null ? (
                      <circle cx={props.cx} cy={props.cy} r={4} fill="#82ca9d" stroke="#fff" strokeWidth={1} />
                    ) : null
                  }
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default VitalStatCarousel;
